import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowRight, PhoneCall, Award, Users, CheckCircle2, ChevronRight, Star, 
  Layers, Settings, ShieldCheck, HeartPulse, Building2, Stethoscope, 
  BriefcaseMedical, Syringe, HardHat, FileText, ChevronLeft
} from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { db } from '../Firebase';
import { collection, doc, updateDoc, onSnapshot } from 'firebase/firestore';

// Custom Numeric Counter Component for On-Scroll Counter Action
function Counter({ value, suffix = '', duration = 1500 }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let start = 0;
    const end = parseInt(value);
    if (start === end) return;

    let totalMiliseconds = duration;
    let incrementTime = Math.abs(Math.floor(totalMiliseconds / end));
    
    let timer = setInterval(() => {
      start += 1;
      setCount(start);
      if (start === end) clearInterval(timer);
    }, incrementTime);

    return () => clearInterval(timer);
  }, [value, duration]);

  return <span>{count}{suffix}</span>;
}

export default function Home({ onOpenConsultation, onOpenEnquiry }) {
  const [activeCategory, setActiveCategory] = useState('MANUAL BED');
  const [currentSlide, setCurrentSlide] = useState(0);

  const heroSlides = [
    {
      image: '/assets/carousal-1.jpg',
      badge: 'Serving Sangli & Maharashtra',
      headline: 'Complete Healthcare Infrastructure & Supply Solutions',
      subheadline: 'Trusted Pharmaceutical Distributor, Hospital Equipment Supplier & Modular OT Setup Specialist serving premier healthcare institutions across Sangli district and Maharashtra.',
      btnPrimary: 'Explore Products',
      btnSecondary: 'Request Consultation'
    },
    {
      image: '/assets/carousal-2-new.jpg',
      badge: 'Trusted Pharmaceutical & Medical Distributor',
      headline: 'Delivering Quality Healthcare Solutions',
      subheadline: 'Supplying genuine medicines, surgical products, and healthcare essentials with reliable distribution, timely delivery, and nationwide reach.',
      btnPrimary: 'Browse Products',
      btnSecondary: 'Request a Quote'
    },
    {
      image: '/assets/carousal-3-new.png',
      badge: 'Integrated ICU furniture',
      headline: 'Premium Intensive Critical Care & Ward Bed Setups',
      subheadline: 'Providing ward comfort with semi-fowler beds, four-function motorized beds, bedside ABS lockers, crash carts, stretchers, and diagnosis systems.',
      btnPrimary: 'Browse ICU Products',
      btnSecondary: 'Get Ward Layout Consultation'
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 3000);
    return () => clearInterval(timer);
  }, [heroSlides.length]);

  const [stats, setStats] = useState([]);
  const [services, setServices] = useState([]);
  const [products, setProducts] = useState([]);
  const [completedProjects, setCompletedProjects] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [categories, setCategories] = useState(['MANUAL BED', 'MOTORIZED BED', 'LED Modular OT Light', 'Modular OT Solutions', 'Medical Equipment']);

  // 1. Listen Stats
  useEffect(() => {
    const statsRef = collection(db, 'stats');
    const unsubscribe = onSnapshot(statsRef, async (snapshot) => {
      let statsList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const experienceStat = statsList.find(s => s.label === 'Years Experience' && s.value === '25');
      if (experienceStat) {
        try {
          const statDocRef = doc(db, 'stats', experienceStat.id);
          await updateDoc(statDocRef, { value: '28' });
        } catch (err) {
          console.error("Error migrating stats experience:", err);
        }
      }
      // Deduplicate stats based on label
      const uniqueStats = [];
      const seen = new Set();
      for (const s of statsList) {
        if (s.label) {
          const norm = s.label.trim().toLowerCase();
          if (!seen.has(norm)) {
            seen.add(norm);
            uniqueStats.push(s);
          }
        }
      }
      setStats(uniqueStats);
    });

    return () => unsubscribe();
  }, []);

  // 2. Listen Services
  useEffect(() => {
    const servicesRef = collection(db, 'services');
    const unsubscribe = onSnapshot(servicesRef, (snapshot) => {
      const servicesList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const uniqueServices = [];
      const seen = new Set();
      for (const s of servicesList) {
        if (s.title) {
          const norm = s.title.trim().toLowerCase();
          if (!seen.has(norm)) {
            seen.add(norm);
            uniqueServices.push(s);
          }
        }
      }
      setServices(uniqueServices);
    });

    return () => unsubscribe();
  }, []);

  // 3. Listen Products
  useEffect(() => {
    const productsRef = collection(db, 'products');
    const unsubscribe = onSnapshot(productsRef, (snapshot) => {
      const productsList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const uniqueProducts = [];
      const seen = new Set();
      for (const p of productsList) {
        if (p.name) {
          const norm = p.name.trim().toLowerCase();
          if (!seen.has(norm)) {
            seen.add(norm);
            uniqueProducts.push(p);
          }
        }
      }
      setProducts(uniqueProducts);
    });

    return () => unsubscribe();
  }, []);

  // 4. Listen Completed Projects
  useEffect(() => {
    const compRef = collection(db, 'completed_projects');
    const unsubscribe = onSnapshot(compRef, (snapshot) => {
      const compsList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      compsList.sort((a, b) => {
        const timeA = a.createdAt ? (a.createdAt.toDate ? a.createdAt.toDate().getTime() : new Date(a.createdAt).getTime()) : 0;
        const timeB = b.createdAt ? (b.createdAt.toDate ? b.createdAt.toDate().getTime() : new Date(b.createdAt).getTime()) : 0;
        return timeB - timeA;
      });
      const uniqueComps = [];
      const seen = new Set();
      for (const c of compsList) {
        if (c.title) {
          const norm = c.title.trim().toLowerCase();
          if (!seen.has(norm)) {
            seen.add(norm);
            uniqueComps.push(c);
          }
        }
      }
      setCompletedProjects(uniqueComps);
    });

    return () => unsubscribe();
  }, []);

  // 5. Listen Blogs
  useEffect(() => {
    const blogsRef = collection(db, 'blogs');
    const unsubscribe = onSnapshot(blogsRef, (snapshot) => {
      const blogsList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const uniqueBlogs = [];
      const seen = new Set();
      for (const b of blogsList) {
        if (b.title) {
          const norm = b.title.trim().toLowerCase();
          if (!seen.has(norm)) {
            seen.add(norm);
            uniqueBlogs.push(b);
          }
        }
      }
      setBlogs(uniqueBlogs);
    });

    return () => unsubscribe();
  }, []);

  // 6. Listen Categories
  useEffect(() => {
    const categoriesRef = collection(db, 'product_categories');
    const unsubscribe = onSnapshot(categoriesRef, (snapshot) => {
      const list = snapshot.docs.map(doc => doc.data().name);
      const uniqueCats = Array.from(new Set(list));
      const allowedCats = ['MANUAL BED', 'MOTORIZED BED', 'LED Modular OT Light', 'Modular OT Solutions', 'Medical Equipment'];
      const filteredCats = allowedCats.filter(cat => uniqueCats.includes(cat));
      setCategories(filteredCats);
    }, (err) => {
      console.error("Error loading categories on Home:", err);
    });

    return () => unsubscribe();
  }, []);

  // Filter products by active category (showing up to 6 items)
  const filteredProducts = products.filter(p => p.category === activeCategory).slice(0, 6);

  const brands = [
    { name: 'Abbott', image: '/assets/partnership%20logo/abbot.png' },
    { name: 'Alkem', image: '/assets/partnership%20logo/alkem.png' },
    { name: 'Amanta', image: '/assets/partnership%20logo/amanta.png' },
    { name: 'Dispo Van', image: '/assets/partnership%20logo/dispo%20van.jpg' },
    { name: 'Dynamic', image: '/assets/partnership%20logo/dynamic.jpg' },
    { name: 'Galderma', image: '/assets/partnership%20logo/galderma.png' },
    { name: 'Hetero', image: '/assets/partnership%20logo/hetero.png' },
    { name: 'JB', image: '/assets/partnership%20logo/jb.jpg' },
    { name: 'Kcare', image: '/assets/partnership%20logo/Kcare.png' },
    { name: 'Mapra', image: '/assets/partnership%20logo/mapra.png' },
    { name: 'Medion', image: '/assets/partnership%20logo/medion.jpg' },
    { name: 'MSN', image: '/assets/partnership%20logo/msn.png' },
    { name: 'Nutragenix', image: '/assets/partnership%20logo/Nutragenix.png' },
    { name: 'Ozone', image: '/assets/partnership%20logo/ozone.png' },
    { name: 'Qualisurge', image: '/assets/partnership%20logo/qualisurge_cover.jpg' },
    { name: 'Rusch', image: '/assets/partnership%20logo/rusch.png' },
    { name: 'Sun', image: '/assets/partnership%20logo/sun.png' },
    { name: 'Torrent', image: '/assets/partnership%20logo/torrent.png' },
    { name: 'Troikaa', image: '/assets/partnership%20logo/troikaa.png' },
    { name: 'USV', image: '/assets/partnership%20logo/usv.png' },
    { name: 'Venus', image: '/assets/partnership%20logo/venus.png' }
  ];

  return (
    <div className="">
      
      {/* 1. HERO CAROUSEL SECTION */}
      <section className="relative min-h-[100vh] flex items-center bg-brand-navy overflow-hidden pt-28 pb-16 grid-mesh">
        {/* Deep blue color washes and backdrop gradient */}
        <div className="absolute inset-0 bg-gradient-to-r from-brand-navy/80 via-brand-navy/40 to-brand-navy/20 z-10" />
        
        {/* Cross-fading background images */}
        <div className="absolute inset-0 z-0 select-none pointer-events-none">
          <AnimatePresence>
            <motion.img
              key={currentSlide}
              src={heroSlides[currentSlide].image}
              alt="Kiran Distributors Infrastructure"
              initial={{ opacity: 0, scale: 1.03 }}
              animate={{ opacity: 0.65, scale: 1 }}
              exit={{ opacity: 0, scale: 0.97 }}
              transition={{ duration: 0.4, ease: "easeInOut" }}
              className="absolute inset-0 w-full h-full object-cover object-center"
            />
          </AnimatePresence>
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-20 w-full">
          <div className="max-w-4xl space-y-6">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentSlide}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -15 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
                className="space-y-6"
              >
                {/* Sliding Badge */}
                <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-brand-blue/20 text-brand-blue border border-brand-blue/30 shadow-inner">
                  <Award size={14} className="text-brand-green shrink-0 animate-pulse-slow" />
                  <span className="text-xs font-bold uppercase tracking-wider font-poppins text-brand-silver">
                    {heroSlides[currentSlide].badge}
                  </span>
                </div>

                {/* Dynamic Heading with highlighted words */}
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold font-montserrat text-white leading-tight">
                  {heroSlides[currentSlide].headline.split(' ').map((word, idx) => {
                    const lowercaseWord = word.toLowerCase();
                    const isHighlighted = lowercaseWord.includes('infrastructure') || 
                                           lowercaseWord.includes('modular') || 
                                           lowercaseWord.includes('critical') || 
                                           lowercaseWord.includes('intensive') ||
                                           lowercaseWord.includes('quality');
                    return isHighlighted ? (
                      <span key={idx} className="text-brand-blue">{word} </span>
                    ) : (
                      <span key={idx}>{word} </span>
                    );
                  })}
                </h1>

                {/* Sliding Description */}
                <p className="text-base sm:text-lg text-brand-silver font-poppins font-light leading-relaxed max-w-3xl">
                  {heroSlides[currentSlide].subheadline}
                </p>

                {/* Slider Action Buttons */}
                <div className="flex flex-wrap gap-4 pt-2">
                  <Link 
                    to="/products" 
                    state={
                      currentSlide === 2 
                        ? { selectedCategory: 'ICU Motorized Beds' } 
                        : undefined
                    }
                    className="btn-primary py-3 px-8 text-sm"
                  >
                    {heroSlides[currentSlide].btnPrimary}
                    <ArrowRight size={16} />
                  </Link>
                  <Link 
                    to="/contact"
                    className="btn-outline py-3 px-8 text-sm border-white/30 text-white hover:bg-white/10 flex items-center justify-center"
                  >
                    {heroSlides[currentSlide].btnSecondary}
                  </Link>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Slider manual arrows */}
        <button
          onClick={() => setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length)}
          aria-label="Previous slide"
          className="absolute left-4 top-1/2 -translate-y-1/2 z-30 p-2.5 rounded-full bg-brand-navy/60 hover:bg-brand-blue text-white border border-white/10 transition-colors hidden md:block hover:scale-105"
        >
          <ChevronLeft size={20} />
        </button>
        <button
          onClick={() => setCurrentSlide((prev) => (prev + 1) % heroSlides.length)}
          aria-label="Next slide"
          className="absolute right-4 top-1/2 -translate-y-1/2 z-30 p-2.5 rounded-full bg-brand-navy/60 hover:bg-brand-blue text-white border border-white/10 transition-colors hidden md:block hover:scale-105"
        >
          <ChevronRight size={20} />
        </button>

        {/* Indicator Dot Bars */}
        <div className="absolute bottom-8 left-6 lg:left-8 z-20 flex gap-2.5">
          {heroSlides.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentSlide(idx)}
              aria-label={`Slide ${idx + 1}`}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                currentSlide === idx ? 'w-8 bg-brand-blue' : 'w-2.5 bg-white/20 hover:bg-white/40'
              }`}
            />
          ))}
        </div>
      </section>

      {/* 2. ABOUT COMPANY PREVIEW */}
      <section className="py-24 bg-white relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
            
            {/* Left: Info Cards Grid instead of Collage */}
            <div className="lg:col-span-6 grid grid-cols-2 gap-6 relative">
              {/* Column 1 */}
              <div className="space-y-6">
                {/* Clinic Image Card */}
                <div className="rounded-3xl shadow-xl overflow-hidden h-80 relative group border border-brand-silver/30 bg-brand-light">
                  <img 
                    src="/assets/about-clinic.png" 
                    alt="Modern Clinic Setup" 
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>

                {/* 28+ Years Stats Card */}
                <div className="bg-brand-navy p-6 rounded-3xl text-white text-center shadow-xl border border-white/5 relative overflow-hidden group hover:border-brand-green transition-all duration-300">
                  <div className="absolute -top-12 -right-12 w-32 h-32 bg-brand-green/10 rounded-full blur-xl group-hover:bg-brand-green/20 transition-all duration-300" />
                  <span className="block text-5xl font-extrabold font-montserrat text-brand-green mb-1.5 drop-shadow-sm group-hover:scale-105 transition-transform duration-300">28+</span>
                  <span className="text-[10px] uppercase font-poppins font-bold tracking-widest text-brand-silver leading-snug block">Years of Sourcing & Distribution Excellence</span>
                </div>
              </div>

              {/* Column 2 */}
              <div className="space-y-6 pt-10">
                {/* Hospital Image Card */}
                <div className="rounded-3xl shadow-xl overflow-hidden h-[380px] relative group border border-brand-silver/30 bg-brand-light">
                  <img 
                    src="/assets/about-kiran-building.jpg" 
                    alt="Kiran Distributors Premise" 
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              </div>
            </div>

            {/* Right: Textual Profile */}
            <div className="lg:col-span-6 space-y-6">
              <div className="border-l-4 border-brand-blue pl-4">
                <span className="text-xs font-bold uppercase tracking-wider text-brand-blue font-poppins block mb-1">Who We Are</span>
                <h2 className="text-3xl sm:text-4xl font-extrabold font-montserrat text-brand-navy">
                  Positioning Hospitals for Medical Success
                </h2>
              </div>
              
              <p className="text-sm text-gray-600 font-poppins leading-relaxed">
                Founded in 1980 by Late Shri Kiran Harakchand Shah, Kiran began as a small retail medical store and has grown into a trusted healthcare partner in the Sangli district. Today, we represent over 40 leading pharmaceutical companies and provide comprehensive solutions in pharmaceutical distribution, hospital furniture, medical equipment, and Modular OT setups.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                <div className="flex gap-2">
                  <CheckCircle2 size={18} className="text-brand-green shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold text-sm text-brand-navy font-montserrat">Trusted Sangli Network</h4>
                    <p className="text-xs text-gray-500 font-poppins">Covering pharmacies, clinics, and tertiary care centres.</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <CheckCircle2 size={18} className="text-brand-green shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold text-sm text-brand-navy font-montserrat">End-to-End Execution</h4>
                    <p className="text-xs text-gray-500 font-poppins">From layout design to installation and equipment supply.</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <CheckCircle2 size={18} className="text-brand-green shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold text-sm text-brand-navy font-montserrat">Multinational Partnerships</h4>
                    <p className="text-xs text-gray-500 font-poppins">Distributor for premium drug & diagnostic manufacturers.</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <CheckCircle2 size={18} className="text-brand-green shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold text-sm text-brand-navy font-montserrat">24/7 Service Support</h4>
                    <p className="text-xs text-gray-500 font-poppins">Experienced engineers supporting vital clinical units.</p>
                  </div>
                </div>
              </div>

              <div className="flex gap-4 pt-4 border-t border-brand-silver/50">
                <Link to="/about" className="btn-secondary text-sm">
                  Read Full History
                  <ArrowRight size={15} />
                </Link>
                <a 
                  href={`https://wa.me/919225836006?text=${encodeURIComponent("Hello Kiran Distributors! I would like to book a Facility Audit for our hospital / medical facility.")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary text-sm flex items-center justify-center gap-2"
                >
                  <PhoneCall size={14} />
                  Book Facility Audit
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. COMPANY STATISTICS SECTION */}
      <section className="py-16 bg-brand-navy text-white relative grid-mesh border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 text-center">
            {stats.map((stat, idx) => (
              <div key={idx} className="space-y-2 border-r last:border-0 border-white/10 px-4">
                <span className="text-3xl sm:text-4xl lg:text-5xl font-extrabold font-montserrat text-brand-green block">
                  <Counter value={stat.value} suffix={stat.suffix} />
                </span>
                <span className="text-xs sm:text-sm font-medium font-poppins text-brand-silver/80 tracking-wide uppercase block">
                  {stat.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. SERVICES SECTION */}
      <section className="py-24 bg-brand-light relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <span className="text-xs font-bold uppercase tracking-widest text-brand-blue font-poppins block">Our Expertise</span>
            <h2 className="text-3xl sm:text-4xl font-extrabold font-montserrat text-brand-navy">
              Comprehensive Healthcare Solution Suites
            </h2>
            <p className="text-sm text-gray-600 font-poppins">
              Providing medical facilities with unmatched clinical setups, surgical supply lines, and precise infrastructure consultation.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((svc, idx) => {
              const IconComponent = LucideIcons[svc.icon] || LucideIcons.Settings;
              return (
                <Link 
                  key={idx}
                  to="/services"
                  state={{ selectedServiceId: svc.id }}
                  className={`service-card-spotlight p-8 rounded-2xl shadow-md border-l-4 ${svc.color || 'border-l-brand-navy'} hover:shadow-xl transition-all duration-300 group hover:-translate-y-1 relative block cursor-pointer`}
                >
                  <div className="w-12 h-12 bg-brand-light group-hover:bg-red-600 text-red-500 group-hover:text-white rounded-xl flex items-center justify-center transition-all duration-300 mb-6 shadow-sm border border-brand-silver/30 group-hover:border-red-600">
                    <IconComponent size={22} className="stroke-[2]" />
                  </div>
                  <h3 className="text-lg font-bold font-montserrat text-brand-navy mb-3 group-hover:text-red-600 transition-colors">
                    {svc.title}
                  </h3>
                  <p className="text-sm text-gray-500 font-poppins leading-relaxed mb-6">
                    {svc.desc}
                  </p>
                  <div 
                    className="inline-flex items-center gap-1 text-xs font-bold font-poppins text-brand-navy group-hover:text-red-600 hover:text-red-700 uppercase tracking-wider transition-colors"
                  >
                    Learn More <ChevronRight size={14} />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* 5. PRODUCTS SHOWCASE */}
      <section className="py-24 bg-white relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div className="space-y-3">
              <span className="text-xs font-bold uppercase tracking-widest text-brand-blue font-poppins block">Product Catalog</span>
              <h2 className="text-3xl sm:text-4xl font-extrabold font-montserrat text-brand-navy">
                Premium Equipment & Supplies Showcase
              </h2>
            </div>
            
            {/* Category tabs scrollable */}
            <div className="flex flex-nowrap gap-2 overflow-x-auto pb-2 border-b border-brand-silver/30 scrollbar-none w-full md:max-w-[60%] shrink-0">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-4 py-2 text-xs font-bold font-poppins rounded-lg transition-colors border shrink-0 ${
                    activeCategory === cat 
                      ? 'bg-brand-blue border-brand-blue text-white shadow-md shadow-brand-blue/10' 
                      : 'bg-white border-brand-silver/50 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          {/* Product showcase grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProducts.map((p) => {
              const categoryIcons = {
                'ICU Beds': HeartPulse,
                'OT Lights': Settings,
                'Surgical Pendants': Layers,
                'Instrument Trolleys': BriefcaseMedical,
                'Bedside Lockers': Building2,
                'Dialysis Chairs': Stethoscope
              };
              const IconComponent = categoryIcons[p.category] || BriefcaseMedical;
              return (
                <div 
                  key={p.id}
                  className="bg-brand-light rounded-2xl border border-brand-silver/30 overflow-hidden shadow-sm hover:shadow-lg transition-shadow group flex flex-col justify-between"
                >
                  <Link 
                    to="/products" 
                    state={{ selectedProduct: p }}
                    className="flex-grow flex flex-col cursor-pointer"
                  >
                    <div 
                      className="h-56 bg-gradient-to-br from-brand-navy to-brand-blue flex items-center justify-center relative group-hover:from-brand-blue group-hover:to-brand-navy transition-all duration-500 w-full"
                      style={p.img && !p.img.startsWith('/assets/logo1.png') ? {
                        backgroundImage: `linear-gradient(to bottom, rgba(17, 17, 17, 0.1), rgba(17, 17, 17, 0.65)), url(${p.img})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center'
                      } : {}}
                    >
                      <div className="absolute inset-0 bg-grid-mesh opacity-10" />
                      {(!p.img || p.img.startsWith('/assets/logo1.png')) && (
                        <div className="w-16 h-16 rounded-2xl bg-white/10 text-brand-green flex items-center justify-center border border-white/20 group-hover:scale-110 transition-transform duration-500">
                          <IconComponent size={32} />
                        </div>
                      )}
                      <div className="absolute top-4 left-4 bg-brand-navy text-brand-green text-[10px] uppercase font-bold tracking-widest px-2.5 py-1 rounded shadow-md border border-white/5 font-poppins">
                        {p.category}
                      </div>
                    </div>
                    
                    <div className="p-6 bg-white pb-0 flex-grow">
                      <h3 className="font-bold text-base text-brand-navy font-montserrat leading-snug group-hover:text-brand-blue transition-colors duration-300">
                        {p.name}
                      </h3>
                    </div>
                  </Link>
                  
                  <div className="p-6 bg-white pt-4">
                    <div className="flex gap-2">
                      <button 
                        onClick={() => onOpenEnquiry(p.name)}
                        className="btn-primary py-2 px-4 text-xs font-poppins flex-grow"
                      >
                        Enquire Price
                      </button>
                      <Link 
                        to="/products"
                        state={{ selectedProduct: p }}
                        className="btn-outline py-2 px-4 text-xs font-poppins border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white"
                      >
                        Full Catalog
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link to="/products" className="btn-secondary py-3 px-8 text-sm inline-flex">
              View All Product Categories
              <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </section>

      {/* 6. INDUSTRIES SERVED */}
      <section className="py-24 bg-brand-light relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          
          <div className="text-center max-w-2xl mx-auto mb-16 space-y-4">
            <span className="text-xs font-bold uppercase tracking-widest text-brand-blue font-poppins block">Sectors & Partners</span>
            <h2 className="text-3xl sm:text-4xl font-extrabold font-montserrat text-brand-navy">
              Serving Healthcare Ecosystems
            </h2>
            <p className="text-sm text-gray-500 font-poppins">
              We deliver custom supply chain standards and heavy infrastructure integration tailored to specialized sectors.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { name: 'Hospitals', icon: Building2, desc: 'Complete multi-specialty ward setup, Modular OT, and layout collaboration with hospital setup architects.' },
              { name: 'Medical Stores & Hospital Pharmacies', icon: BriefcaseMedical, desc: 'Regular batch distribution of quality medicines and surgical consumables.' },
              { name: 'Nursing Homes', icon: HeartPulse, desc: 'Economic ICU setups, furniture, and critical care units.' },
              { name: 'Diagnostic Centers', icon: Stethoscope, desc: 'Advanced equipment, procedural chairs, and accessories.' },
              { name: 'Healthcare Institutes', icon: Award, desc: 'Anatomical layouts, simulation labs, and dental setups.' },
              { name: 'Specialty Clinics', icon: Syringe, desc: 'Specialized diagnostic systems and modular partitions.' }
            ].map((ind, idx) => (
              <div 
                key={idx}
                className="bg-white p-8 rounded-3xl border border-brand-silver/30 shadow-md text-center space-y-4 group hover:border-brand-blue hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className="w-16 h-16 bg-brand-light text-brand-blue rounded-2xl flex items-center justify-center mx-auto transition-transform group-hover:scale-110 shadow-sm">
                  <ind.icon size={28} />
                </div>
                <h3 className="font-bold text-lg text-brand-navy font-montserrat">{ind.name}</h3>
                <p className="text-sm text-gray-500 font-poppins leading-relaxed">{ind.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 7. WHY CHOOSE KIRAN DISTRIBUTORS */}
      <section className="py-24 bg-white relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
            <div className="lg:col-span-5 space-y-6">
              <span className="text-xs font-bold uppercase tracking-widest text-brand-blue font-poppins block">Our Edge</span>
              <h2 className="text-3xl sm:text-4xl font-extrabold font-montserrat text-brand-navy leading-tight">
                Sangli's Most Trusted Medical Infrastructure Partner
              </h2>
              <p className="text-sm text-gray-600 font-poppins leading-relaxed">
                By maintaining absolute sourcing integrity, clinical accuracy, and expert installation guidance, we enable healthcare operators to focus entirely on patient recovery.
              </p>
              
              <div className="p-5 rounded-2xl bg-brand-light border border-brand-silver flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-brand-green text-white flex items-center justify-center shrink-0">
                  <ShieldCheck size={20} />
                </div>
                <div>
                  <h4 className="font-bold text-sm text-brand-navy font-montserrat leading-none mb-1">FDA & ISO Certified Pipeline</h4>
                  <span className="text-xs text-gray-500 font-poppins">Products comply with strict WHO-GMP and medical standards.</span>
                </div>
              </div>
            </div>

            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-6">
              {[
                { title: 'Medical & Surgical Distributors', text: 'For more than 25 pharmaceutical companies.' },
                { title: 'Genuine Medical Products', text: '100% genuine sourcing directly from audited FDA-registered brands.' },
                { title: 'End-to-End OT Planning', text: 'Professional drawing analysis, modular panel selection, and testing.' },
                { title: 'Timely Delivery Network', text: 'Robust logistical network keeping hospital inventories fully supplied.' },
                { title: 'Expert Technical Staff', text: 'Trained technicians for medical gas systems & ICU configurations.' },
                { title: 'Hospital Layout Advice', text: 'Consultation to maximize floor utility and clear hospital accreditation audits.' }
              ].map((edge, idx) => (
                <div key={idx} className="bg-brand-light border border-brand-silver p-8 rounded-3xl space-y-3 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md">
                  <div className="w-7 h-7 rounded-full bg-brand-blue/10 text-brand-blue flex items-center justify-center">
                    <CheckCircle2 size={16} className="stroke-[3]" />
                  </div>
                  <h4 className="font-bold text-base sm:text-lg text-brand-navy font-montserrat">{edge.title}</h4>
                  <p className="text-sm text-gray-500 font-poppins leading-relaxed">{edge.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 8. FEATURED PROJECTS */}
      <section className="py-24 bg-brand-light relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
            <div className="space-y-3">
              <span className="text-xs font-bold uppercase tracking-widest text-brand-blue font-poppins block">Our Footprint</span>
              <h2 className="text-3xl sm:text-4xl font-extrabold font-montserrat text-brand-navy">
                Completed Infrastructure Projects
              </h2>
            </div>
            
            <Link to="/projects" className="btn-outline py-2.5 px-6 text-sm border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white">
              View All Projects
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {completedProjects.slice(0, 3).map((p, idx) => {
              const gradients = [
                'from-brand-navy to-brand-blue',
                'from-brand-navy to-brand-green',
                'from-brand-navy to-blue-900'
              ];
              const gradient = gradients[idx % gradients.length];

              return (
                <div key={p.id || idx} className="bg-white rounded-2xl overflow-hidden shadow-md border border-brand-silver/30 flex flex-col justify-between hover:shadow-lg transition-shadow group">
                  <Link 
                    to="/projects" 
                    state={{ selectedProjectId: p.id }}
                    className="flex-grow flex flex-col cursor-pointer"
                  >
                    <div className="h-40 relative overflow-hidden flex items-center justify-center bg-brand-light w-full">
                      {p.image && !p.image.startsWith('/assets/logo1.png') ? (
                        <img 
                          src={p.image} 
                          alt={p.title} 
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      ) : (
                        <div className={`absolute inset-0 bg-gradient-to-br ${gradient} flex items-center justify-center`}>
                          <div className="absolute inset-0 bg-grid-mesh opacity-10" />
                          <div className="w-12 h-12 rounded-xl bg-white/10 text-white flex items-center justify-center border border-white/20">
                            <Building2 size={24} />
                          </div>
                        </div>
                      )}
                      <div className="absolute top-4 left-4 bg-brand-navy text-brand-green text-[10px] uppercase font-bold tracking-widest px-2.5 py-1 rounded shadow-md border border-white/5 font-poppins z-10">
                        {p.location || 'Completed'}
                      </div>
                    </div>
                    <div className="p-6 flex-grow space-y-4">
                      <div>
                        <h3 className="font-extrabold text-base text-brand-navy font-montserrat group-hover:text-brand-blue transition-colors duration-300">{p.title}</h3>
                        <span className="text-xs text-brand-blue font-medium font-poppins block mt-0.5">{p.authority}</span>
                      </div>
                      <p className="text-xs text-gray-500 font-poppins leading-relaxed line-clamp-3">{p.desc}</p>
                    </div>
                  </Link>
                  <div className="p-6 pt-0 border-t border-gray-50 bg-gray-50/50 flex justify-between items-center text-xs font-semibold font-poppins">
                    <span className="text-brand-green">100% Completed</span>
                    <Link 
                      to="/projects" 
                      state={{ selectedProjectId: p.id }}
                      className="text-brand-navy hover:text-brand-blue flex items-center gap-1"
                    >
                      Details <ChevronRight size={14} />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>


      {/* 11. HEALTHCARE CONSULTATION CTA */}
      <section className="py-20 bg-brand-blue text-white relative overflow-hidden">
        {/* Soft decorative visual shape */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-brand-green/20 rounded-full blur-[120px] pointer-events-none" />
        
        <div className="max-w-5xl mx-auto px-6 text-center relative z-10 space-y-6">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold font-montserrat text-white leading-tight">
            Planning a New Hospital or Healthcare Facility?
          </h2>
          <p className="text-sm sm:text-base text-blue-50 font-poppins max-w-3xl mx-auto leading-relaxed">
            Get expert, compliant layout advice, surgical equipment integration matrices, and fully customizable hospital furniture packages designed directly for Sangli and Maharashtra healthcare environments.
          </p>
          <div className="flex flex-wrap gap-4 justify-center pt-4">
            <a 
              href={`https://wa.me/919225836006?text=${encodeURIComponent("Hello Kiran Distributors! I am planning a new hospital/healthcare facility and would like to inquire about your infrastructure consultation services.")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary py-3 px-8 text-sm bg-brand-navy hover:bg-brand-navy/90 text-white flex items-center justify-center gap-2"
            >
              <PhoneCall size={16} />
              Book Infrastructure Consultation
            </a>
            <Link 
              to="/contact" 
              className="btn-outline py-3 px-8 text-sm border-white text-white hover:bg-white hover:text-brand-blue"
            >
              Get In Touch
            </Link>
          </div>
        </div>
      </section>


      {/* 13. PARTNER BRANDS SECTION */}
      <section className="py-20 bg-brand-light border-t border-brand-silver/30 overflow-hidden relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 mb-12 text-center space-y-3">
          <span className="text-xs font-bold uppercase tracking-widest text-brand-blue font-poppins block">World-Class Healthcare Brands</span>
          <h2 className="text-3xl font-extrabold font-montserrat text-brand-navy">
            Authorized Sourcing Partners
          </h2>
          <p className="text-sm text-gray-500 font-poppins max-w-xl mx-auto">
            We collaborate directly with leading global and national medical manufacturers to deliver verified, certified clinical infrastructure.
          </p>
        </div>
        
        {/* Infinite sliding logo loop */}
        <div className="w-full flex select-none overflow-hidden relative py-4">
          {/* Left/Right Fade Masks */}
          <div className="absolute inset-y-0 left-0 w-24 sm:w-48 bg-gradient-to-r from-brand-light via-brand-light/80 to-transparent z-10 pointer-events-none" />
          <div className="absolute inset-y-0 right-0 w-24 sm:w-48 bg-gradient-to-l from-brand-light via-brand-light/80 to-transparent z-10 pointer-events-none" />

          <div className="animate-logo-slider flex gap-8 items-center">
            {brands.concat(brands).map((brand, idx) => (
              <div 
                key={idx} 
                className="w-60 h-24 bg-white border border-brand-silver/40 rounded-2xl flex items-center justify-center shadow-sm select-none shrink-0 transition-all duration-300 hover:shadow-lg hover:border-brand-blue/30 group cursor-pointer hover:-translate-y-1"
              >
                {brand.image ? (
                  <img 
                    src={brand.image} 
                    alt={brand.name} 
                    className="max-h-[70%] max-w-[80%] object-contain opacity-90 group-hover:opacity-100 transition-all duration-300 transform group-hover:scale-105"
                  />
                ) : (
                  <span className="px-4 text-center text-sm font-semibold text-brand-navy/60 group-hover:text-brand-blue transition-colors duration-300">{brand.name}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
}
